// File: /js/shared/bootstrap/initSharedUI.js

import { handleAjaxSubmit }  from '/js/shared/handlers/handleAjaxSubmit.js';
import { handleAjaxClick }   from '/js/shared/handlers/handleAjaxClick.js';
import { handleAjaxChange }  from '/js/shared/handlers/handleAjaxChange.js';
import { handleAjaxOnInput } from '/js/shared/handlers/handleAjaxOnInput.js';
import { handleUiClick }     from '/js/shared/handlers/handleUiClick.js';
import { handleUiChange }    from '/js/shared/handlers/handleUiChange.js';
import { toggleTabs, toggleModalVisibility } from '/js/shared/utils/DOMUtils.js';
import { initHeaderAutohide } from '/js/shared/components/headerAutohide.js';
import { initKprimeSliders } from '/js/shared/quiz/kprime-slider.js';
import { initA2hsOnboarding } from '/js/shared/components/a2hsOnboarding.js';
import { initPushOptIn } from '/js/shared/components/pushOptIn.js';
import { initPageViewBeacon } from '/js/shared/components/pageViewBeacon.js';
import { pwaEnabled } from '/js/shared/utils/config.js';
// Night mode: side-effect import — registers the 'night-toggle' data-ui
// click handler on the domExtensions registry at module load.
import '/js/shared/components/nightMode.js';

export function initSharedUI() {
  // 1) Intercept any <form data-ajax-submit="true"> for AJAX
  document.addEventListener('submit', handleAjaxSubmit);

  // 2) Handle any click on elements opted into AJAX clicks
  document.addEventListener('click', handleAjaxClick);

  // 3) Handle any change on elements opted into AJAX changes
  document.addEventListener('change', handleAjaxChange);
  
  // 4) Handle any input on elements opted into AJAX changes
  document.addEventListener('input', handleAjaxOnInput);

	// 4bis) Handle input in the forme of paste
	document.addEventListener('paste', (e) => {
  		setTimeout(() => handleAjaxOnInput(e), 0);
	});

  // 5) UI-only handlers (modals, toggles, etc.)
  document.addEventListener('click', handleUiClick);
  document.addEventListener('change', handleUiChange);

  // 6) Auto-hide the sticky header while reading on content/module pages
  //    (mobile only). Self-gates by viewport + body[data-page]; inert
  //    elsewhere. See components/headerAutohide.js.
  initHeaderAutohide();

  // 7) Kprime participant sliders (answer_format='kprime'). Element-scoped,
  //    no-op when no `[data-kprime-slider]` is present. See quiz/kprime-slider.js.
  initKprimeSliders();

  // 8) App track / PWA (App P1, 2026-07) — ENTIRELY gated behind the
  //    server-emitted pwaEnabled flag (Env::pwaEnabled(); AD hard rule
  //    2026-07-05: hidden on prod until the store launch, testing on
  //    dev + staging only). Flag ON: register the service worker
  //    (static-asset cache + offline fallback only — see public/sw.js),
  //    the iOS Add-to-Home-Screen onboarding card, and the web-push
  //    opt-in (installed-app first-launch card + profile-tab control;
  //    its click handlers register on the data-ui registry at module
  //    load, harmless while the markup never renders). Flag OFF:
  //    actively UNREGISTER any previously-registered SW so browsers
  //    that saw the flag-on window return to the plain-website state.
  if (pwaEnabled()) {
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js').catch((err) => {
          console.warn('Service worker registration failed:', err);
        });
      });
    }
    initA2hsOnboarding();
    initPushOptIn();
  } else if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations()
      .then((registrations) => registrations.forEach((r) => r.unregister()))
      .catch(() => { /* best-effort cleanup */ });
  }

  // 8bis) Human-confirmation beacon (activity measurement, 2026-08-12):
  //    one fire-and-forget `confirmPageView` POST per page load, stamping
  //    this render's page_view row as confirmed-human (non-JS bots never
  //    send it). Silent by contract; no-ops server-side on unlogged pages.
  initPageViewBeacon();

  // 9) Restore the active tab from a `#tab-<panelId>` URL fragment
  //    (written by the 'tab-switch' handler in domExtensions.js). Makes the
  //    selected tab survive back-navigation from a content page — the ←
  //    arrow and browser back both land on the listing with the fragment
  //    intact. Inert when the fragment doesn't name an existing tab.
  const tabFragment = window.location.hash.match(/^#tab-(.+)$/);
  if (tabFragment) {
    const tab = document.querySelector(
      `[data-ui="tab-switch"][data-target="#${tabFragment[1]}"]`
    );
    if (tab) toggleTabs(tab);
  }

  // 9bis) Login-modal deep link (ALAMU flyer QR, 2026-10): `#inscription`
  //    / `#connexion` opens the header login modal on that tab, by driving
  //    the existing `toggle-modal` + `tab-switch` triggers. The fragment is
  //    then dropped so a reload doesn't reopen the modal. Inert when logged
  //    in (LoginPopup renders no modal) and for any other fragment.
  const loginFragments = { '#inscription': '#login-tabs-subscribe', '#connexion': '#login-tabs-login' };
  const loginTab = loginFragments[window.location.hash];
  if (loginTab) {
    const opener = document.querySelector('[data-ui="toggle-modal"][data-target="#login-modal"]');
    const tab = document.querySelector(`[data-ui="tab-switch"][data-target="${loginTab}"]`);
    if (opener && tab) {
      toggleTabs(tab);
      toggleModalVisibility(opener);
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }

  // 10) (other shared features: toasts…)
}
